import {
  Box,
  Center,
  Flex,
  GridItem,
  HStack,
  Image,
  Link,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { Db } from '@diamond/shared/schema';
import { IconButton } from '@diamond/shared/ui';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { AdminLayout } from './admin-layout';

export type AdminMenu = {
  id: number;
  href: string;
  label: string;
  icon: OverridableComponent<SvgIconTypeMap<unknown, 'svg'>> & {
    muiName: string;
  };

  requiredRoles: Db['users']['role'][];
};

export type AdminMenuList = Array<AdminMenu>;

type AdminMenuListLayoutProps = { menus: AdminMenuList };

export function AdminMenuListLayout({ menus }: AdminMenuListLayoutProps) {
  const { userRole } = useAuthStore();
  const currentRole = userRole as Db['users']['role'];

  const filteredMenu = useMemo(() => {
    return menus.filter((menu) => menu.requiredRoles.includes(currentRole));
  }, [currentRole, menus]);

  return (
    <AdminLayout>
      <Box mb={20}>
        <Flex flex={1} display={{ base: 'flex', md: 'flex' }}>
          <Box overflow="hidden" flex={1} rounded="md" m="unset">
            <Image
              src="/assets/images/admin-banner-home.png"
              alt="Admin Homepage Banner"
              objectFit="cover"
              height={{ base: 160, md: 300 }}
            />
          </Box>
        </Flex>

        <Box mt={8}>
          <SimpleGrid
            gridTemplateColumns="repeat(auto-fill, minmax(250px, 1fr))"
            spacing={6}
          >
            {filteredMenu.map((menu) => (
              <GridItem
                key={menu.id}
                w="100%"
                h="96px"
                borderRadius="md"
                border="1px"
                _hover={{ bg: 'gray.50' }}
              >
                <Link as={RouterLink} to={menu.href}>
                  <HStack
                    spacing={4}
                    py={2.5}
                    px={2.5}
                    bg="transparent"
                    borderRadius="md"
                    cursor="pointer"
                  >
                    <Center
                      bg="white"
                      borderRadius="md"
                      color="blue"
                      border="1px"
                      w="72px"
                      h="72px"
                    >
                      <IconButton
                        name={menu.label}
                        icon={<menu.icon fontSize="large" />}
                        aria-label={menu.label}
                        variant="unstyled"
                        width={20}
                      />
                    </Center>
                    <Box>
                      <Text fontSize="lg" fontWeight="semibold">
                        {menu.label}
                      </Text>
                    </Box>
                  </HStack>
                </Link>
              </GridItem>
            ))}
          </SimpleGrid>
        </Box>
      </Box>
    </AdminLayout>
  );
}
